import { supabase } from '../supabase/supabaseClient';
import { logger, log } from '../utils/logUtils';

/**
 * Check if a user has a specific role
 * @param userId The user ID to check
 * @param roleName The role name to check for
 * @returns Promise<boolean> True if the user has the role, false otherwise
 */
export const hasRole = async (userId: string, roleName: string): Promise<boolean> => {
  // log('hasRole called with:', { userId, roleName });
  
  try {
    // Use a regular query instead of .single() to avoid 406 errors
    const { data, error } = await supabase
      .from('user_roles')
      .select('*')
      .eq('user_id', userId)
      .eq('role', roleName);
    
    if (error) {
      logger.error('Error in hasRole query:', error);
      return false;
    }
    
    // Check if any records were returned
    const result = data && data.length > 0;
    // log('hasRole result:', result, data);
    return result;
  } catch (err) {
    logger.error('Exception in hasRole:', err);
    return false;
  }
};


/**
 * Check if a user is an admin
 * @param userId The user ID to check
 * @returns Promise<boolean> True if the user is an admin, false otherwise
 */
export const isUserAdmin = async (userId: string): Promise<boolean> => {
  return hasRole(userId, 'admin');
};

/**
 * Get all roles for a user
 * @param userId The user ID to get roles for
 * @returns Promise<string[]> Array of role names
 */
export const getUserRoles = async (userId: string): Promise<string[]> => {
  if (!userId) return [];
  
  try {
    const { data, error } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', userId);
      
    if (error) {
      logger.error('Error getting user roles:', error);
      return [];
    }
    
    return data.map(row => row.role);
  } catch (err) {
    logger.error('Exception getting user roles:', err);
    return [];
  }
};

/**
 * Assign a role to a user
 * @param userId The user ID to assign the role to
 * @param roleName The role name to assign
 * @returns Promise<boolean> True if successful, false otherwise
 */
export const assignRole = async (userId: string, roleName: string): Promise<boolean> => {
  if (!userId) return false;
  
  try {
    // Check if the role assignment already exists
    const { data: existingRole } = await supabase
      .from('user_roles')
      .select('id')
      .eq('user_id', userId)
      .eq('role', roleName)
      .single();
      
    if (existingRole) {
      // Role already assigned
      return true;
    }
    
    // Add the new role
    const { error } = await supabase
      .from('user_roles')
      .insert({ user_id: userId, role: roleName });
      
    if (error) {
      logger.error('Error assigning role:', error);
      return false;
    }
    
    return true;
  } catch (err) {
    logger.error('Exception assigning role:', err);
    return false;
  }
};

/**
 * Remove a role from a user
 * @param userId The user ID to remove the role from
 * @param roleName The role name to remove
 * @returns Promise<boolean> True if successful, false otherwise
 */
export const removeRole = async (userId: string, roleName: string): Promise<boolean> => {
  if (!userId) return false;
  
  try {
    const { error } = await supabase
      .from('user_roles')
      .delete()
      .eq('user_id', userId)
      .eq('role', roleName);
      
    if (error) {
      logger.error('Error removing role:', error);
      return false;
    }
    
    return true;
  } catch (err) {
    logger.error('Exception removing role:', err);
    return false;
  }
};
